import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { TZDate } from "@date-fns/tz";
import { addDays, addMonths, addWeeks, addYears, differenceInCalendarDays, differenceInCalendarMonths, eachMonthOfInterval, eachYearOfInterval, endOfISOWeek, endOfMonth, endOfWeek, endOfYear, format, getISOWeek, getMonth, getWeek, getYear, isAfter, isBefore, isDate, isSameDay, isSameMonth, isSameYear, max, min, setMonth, setYear, startOfDay, startOfISOWeek, startOfMonth, startOfWeek, startOfYear } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { endOfBroadcastWeek } from "../helpers/endOfBroadcastWeek.js";
import { startOfBroadcastWeek } from "../helpers/startOfBroadcastWeek.js";
/**
 * A wrapper class around [date-fns](http://date-fns.org) that provides utility
 * methods for date manipulation and formatting.
 *
 * @since 9.2.0
 * @example
 *   const dateLib = new DateLib({ locale: es });
 *   const newDate = dateLib.addDays(new Date(), 5);
 */
export class DateLib {
  /**
   * Creates an instance of `DateLib`.
   *
   * @param options Configuration options for the date library.
   * @param overrides Custom overrides for the date library functions.
   */
  constructor(options, overrides) {
    /**
     * Reference to the built-in Date constructor.
     *
     * @deprecated Use `newDate()` or `today()`.
     */
    this.Date = Date;
    /**
     * Creates a new `Date` object representing today's date.
     *
     * @since 9.5.0
     * @returns A `Date` object for today's date.
     */
    this.today = () => {
      var _this$overrides;
      if ((_this$overrides = this.overrides) !== null && _this$overrides !== void 0 && _this$overrides.today) {
        return this.overrides.today();
      }
      if (this.options.timeZone) {
        return TZDate.tz(this.options.timeZone);
      }
      return new this.Date();
    };
    /**
     * Creates a new `Date` object with the specified year, month, and day.
     *
     * @since 9.5.0
     * @param year The year.
     * @param monthIndex The month (0-11).
     * @param date The day of the month.
     * @returns A new `Date` object.
     */
    this.newDate = (year, monthIndex, date) => {
      var _this$overrides2;
      if ((_this$overrides2 = this.overrides) !== null && _this$overrides2 !== void 0 && _this$overrides2.newDate) {
        return this.overrides.newDate(year, monthIndex, date);
      }
      if (this.options.timeZone) {
        return new TZDate(year, monthIndex, date, this.options.timeZone);
      }
      return new Date(year, monthIndex, date);
    };
    /**
     * Adds the specified number of days to the given date.
     *
     * @param date The date to add days to.
     * @param amount The number of days to add.
     * @returns The new date with the days added.
     */
    this.addDays = (date, amount) => {
      var _this$overrides3;
      return (_this$overrides3 = this.overrides) !== null && _this$overrides3 !== void 0 && _this$overrides3.addDays ? this.overrides.addDays(date, amount) : addDays(date, amount);
    };
    /**
     * Adds the specified number of months to the given date.
     *
     * @param date The date to add months to.
     * @param amount The number of months to add.
     * @returns The new date with the months added.
     */
    this.addMonths = (date, amount) => {
      var _this$overrides4;
      return (_this$overrides4 = this.overrides) !== null && _this$overrides4 !== void 0 && _this$overrides4.addMonths ? this.overrides.addMonths(date, amount) : addMonths(date, amount);
    };
    /**
     * Adds the specified number of weeks to the given date.
     *
     * @param date The date to add weeks to.
     * @param amount The number of weeks to add.
     * @returns The new date with the weeks added.
     */
    this.addWeeks = (date, amount) => {
      var _this$overrides5;
      return (_this$overrides5 = this.overrides) !== null && _this$overrides5 !== void 0 && _this$overrides5.addWeeks ? this.overrides.addWeeks(date, amount) : addWeeks(date, amount);
    };
    /**
     * Adds the specified number of years to the given date.
     *
     * @param date The date to add years to.
     * @param amount The number of years to add.
     * @returns The new date with the years added.
     */
    this.addYears = (date, amount) => {
      var _this$overrides6;
      return (_this$overrides6 = this.overrides) !== null && _this$overrides6 !== void 0 && _this$overrides6.addYears ? this.overrides.addYears(date, amount) : addYears(date, amount);
    };
    /**
     * Returns the number of calendar days between the given dates.
     *
     * @param dateLeft The later date.
     * @param dateRight The earlier date.
     * @returns The number of calendar days between the dates.
     */
    this.differenceInCalendarDays = (dateLeft, dateRight) => {
      var _this$overrides7;
      return (_this$overrides7 = this.overrides) !== null && _this$overrides7 !== void 0 && _this$overrides7.differenceInCalendarDays ? this.overrides.differenceInCalendarDays(dateLeft, dateRight) : differenceInCalendarDays(dateLeft, dateRight);
    };
    /**
     * Returns the number of calendar months between the given dates.
     *
     * @param dateLeft The later date.
     * @param dateRight The earlier date.
     * @returns The number of calendar months between the dates.
     */
    this.differenceInCalendarMonths = (dateLeft, dateRight) => {
      var _this$overrides8;
      return (_this$overrides8 = this.overrides) !== null && _this$overrides8 !== void 0 && _this$overrides8.differenceInCalendarMonths ? this.overrides.differenceInCalendarMonths(dateLeft, dateRight) : differenceInCalendarMonths(dateLeft, dateRight);
    };
    /**
     * Returns the months between the given dates.
     *
     * @param interval The interval to get the months for.
     */
    this.eachMonthOfInterval = interval => {
      var _this$overrides9;
      return (_this$overrides9 = this.overrides) !== null && _this$overrides9 !== void 0 && _this$overrides9.eachMonthOfInterval ? this.overrides.eachMonthOfInterval(interval) : eachMonthOfInterval(interval);
    };
    /**
     * Returns the years between the given dates.
     *
     * @since 9.11.1
     * @param interval The interval to get the years for.
     * @returns The array of years in the interval.
     */
    this.eachYearOfInterval = interval => {
      var _this$overrides10;
      const years = (_this$overrides10 = this.overrides) !== null && _this$overrides10 !== void 0 && _this$overrides10.eachYearOfInterval ? this.overrides.eachYearOfInterval(interval) : eachYearOfInterval(interval);
      // Remove duplicates that may happen across DST transitions (e.g., "America/Sao_Paulo")
      // See https://github.com/date-fns/tz/issues/72
      const uniqueYears = new Set(years.map(d => this.getYear(d)));
      if (uniqueYears.size === years.length) {
        // No duplicates, return as is
        return years;
      }
      // Rebuild the array to ensure one date per year
      const yearsArray = [];
      uniqueYears.forEach(y => {
        yearsArray.push(new Date(y, 0, 1));
      });
      return yearsArray;
    };
    /**
     * Returns the end of the broadcast week for the given date.
     *
     * @param date The original date.
     * @returns The end of the broadcast week.
     */
    this.endOfBroadcastWeek = date => {
      var _this$overrides11;
      return (_this$overrides11 = this.overrides) !== null && _this$overrides11 !== void 0 && _this$overrides11.endOfBroadcastWeek ? this.overrides.endOfBroadcastWeek(date) : endOfBroadcastWeek(date, this);
    };
    /**
     * Returns the end of the ISO week for the given date.
     *
     * @param date The original date.
     * @returns The end of the ISO week.
     */
    this.endOfISOWeek = date => {
      var _this$overrides12;
      return (_this$overrides12 = this.overrides) !== null && _this$overrides12 !== void 0 && _this$overrides12.endOfISOWeek ? this.overrides.endOfISOWeek(date) : endOfISOWeek(date);
    };
    /**
     * Returns the end of the month for the given date.
     *
     * @param date The original date.
     * @returns The end of the month.
     */
    this.endOfMonth = date => {
      var _this$overrides13;
      return (_this$overrides13 = this.overrides) !== null && _this$overrides13 !== void 0 && _this$overrides13.endOfMonth ? this.overrides.endOfMonth(date) : endOfMonth(date);
    };
    /**
     * Returns the end of the week for the given date.
     *
     * @param date The original date.
     * @returns The end of the week.
     */
    this.endOfWeek = (date, options) => {
      var _this$overrides14;
      return (_this$overrides14 = this.overrides) !== null && _this$overrides14 !== void 0 && _this$overrides14.endOfWeek ? this.overrides.endOfWeek(date, options) : endOfWeek(date, this.options);
    };
    /**
     * Returns the end of the year for the given date.
     *
     * @param date The original date.
     * @returns The end of the year.
     */
    this.endOfYear = date => {
      var _this$overrides15;
      return (_this$overrides15 = this.overrides) !== null && _this$overrides15 !== void 0 && _this$overrides15.endOfYear ? this.overrides.endOfYear(date) : endOfYear(date);
    };
    /**
     * Formats the given date using the specified format string.
     *
     * @param date The date to format.
     * @param formatStr The format string.
     * @returns The formatted date string.
     */
    this.format = (date, formatStr, _options) => {
      var _this$overrides16;
      const formatted = (_this$overrides16 = this.overrides) !== null && _this$overrides16 !== void 0 && _this$overrides16.format ? this.overrides.format(date, formatStr, this.options) : format(date, formatStr, this.options);
      if (this.options.numerals && this.options.numerals !== "latn") {
        return this.replaceDigits(formatted);
      }
      return formatted;
    };
    /**
     * Returns the ISO week number for the given date.
     *
     * @param date The date to get the ISO week number for.
     * @returns The ISO week number.
     */
    this.getISOWeek = date => {
      var _this$overrides17;
      return (_this$overrides17 = this.overrides) !== null && _this$overrides17 !== void 0 && _this$overrides17.getISOWeek ? this.overrides.getISOWeek(date) : getISOWeek(date);
    };
    /**
     * Returns the month of the given date.
     *
     * @param date The date to get the month for.
     * @returns The month.
     */
    this.getMonth = (date, _options) => {
      var _this$overrides18;
      return (_this$overrides18 = this.overrides) !== null && _this$overrides18 !== void 0 && _this$overrides18.getMonth ? this.overrides.getMonth(date, this.options) : getMonth(date, this.options);
    };
    /**
     * Returns the year of the given date.
     *
     * @param date The date to get the year for.
     * @returns The year.
     */
    this.getYear = (date, _options) => {
      var _this$overrides19;
      return (_this$overrides19 = this.overrides) !== null && _this$overrides19 !== void 0 && _this$overrides19.getYear ? this.overrides.getYear(date, this.options) : getYear(date, this.options);
    };
    /**
     * Returns the local week number for the given date.
     *
     * @param date The date to get the week number for.
     * @returns The week number.
     */
    this.getWeek = (date, _options) => {
      var _this$overrides20;
      return (_this$overrides20 = this.overrides) !== null && _this$overrides20 !== void 0 && _this$overrides20.getWeek ? this.overrides.getWeek(date, this.options) : getWeek(date, this.options);
    };
    /**
     * Checks if the first date is after the second date.
     *
     * @param date The date to compare.
     * @param dateToCompare The date to compare with.
     * @returns True if the first date is after the second date.
     */
    this.isAfter = (date, dateToCompare) => {
      var _this$overrides21;
      return (_this$overrides21 = this.overrides) !== null && _this$overrides21 !== void 0 && _this$overrides21.isAfter ? this.overrides.isAfter(date, dateToCompare) : isAfter(date, dateToCompare);
    };
    /**
     * Checks if the first date is before the second date.
     *
     * @param date The date to compare.
     * @param dateToCompare The date to compare with.
     * @returns True if the first date is before the second date.
     */
    this.isBefore = (date, dateToCompare) => {
      var _this$overrides22;
      return (_this$overrides22 = this.overrides) !== null && _this$overrides22 !== void 0 && _this$overrides22.isBefore ? this.overrides.isBefore(date, dateToCompare) : isBefore(date, dateToCompare);
    };
    /**
     * Checks if the given value is a Date object.
     *
     * @param value The value to check.
     * @returns True if the value is a Date object.
     */
    this.isDate = value => {
      var _this$overrides23;
      return (_this$overrides23 = this.overrides) !== null && _this$overrides23 !== void 0 && _this$overrides23.isDate ? this.overrides.isDate(value) : isDate(value);
    };
    /**
     * Checks if the given dates are on the same day.
     *
     * @param dateLeft The first date to compare.
     * @param dateRight The second date to compare.
     * @returns True if the dates are on the same day.
     */
    this.isSameDay = (dateLeft, dateRight) => {
      var _this$overrides24;
      return (_this$overrides24 = this.overrides) !== null && _this$overrides24 !== void 0 && _this$overrides24.isSameDay ? this.overrides.isSameDay(dateLeft, dateRight) : isSameDay(dateLeft, dateRight);
    };
    /**
     * Checks if the given dates are in the same month.
     *
     * @param dateLeft The first date to compare.
     * @param dateRight The second date to compare.
     * @returns True if the dates are in the same month.
     */
    this.isSameMonth = (dateLeft, dateRight) => {
      var _this$overrides25;
      return (_this$overrides25 = this.overrides) !== null && _this$overrides25 !== void 0 && _this$overrides25.isSameMonth ? this.overrides.isSameMonth(dateLeft, dateRight) : isSameMonth(dateLeft, dateRight);
    };
    /**
     * Checks if the given dates are in the same year.
     *
     * @param dateLeft The first date to compare.
     * @param dateRight The second date to compare.
     * @returns True if the dates are in the same year.
     */
    this.isSameYear = (dateLeft, dateRight) => {
      var _this$overrides26;
      return (_this$overrides26 = this.overrides) !== null && _this$overrides26 !== void 0 && _this$overrides26.isSameYear ? this.overrides.isSameYear(dateLeft, dateRight) : isSameYear(dateLeft, dateRight);
    };
    /**
     * Returns the latest date in the given array of dates.
     *
     * @param dates The array of dates to compare.
     * @returns The latest date.
     */
    this.max = dates => {
      var _this$overrides27;
      return (_this$overrides27 = this.overrides) !== null && _this$overrides27 !== void 0 && _this$overrides27.max ? this.overrides.max(dates) : max(dates);
    };
    /**
     * Returns the earliest date in the given array of dates.
     *
     * @param dates The array of dates to compare.
     * @returns The earliest date.
     */
    this.min = dates => {
      var _this$overrides28;
      return (_this$overrides28 = this.overrides) !== null && _this$overrides28 !== void 0 && _this$overrides28.min ? this.overrides.min(dates) : min(dates);
    };
    /**
     * Sets the month of the given date.
     *
     * @param date The date to set the month on.
     * @param month The month to set (0-11).
     * @returns The new date with the month set.
     */
    this.setMonth = (date, month) => {
      var _this$overrides29;
      return (_this$overrides29 = this.overrides) !== null && _this$overrides29 !== void 0 && _this$overrides29.setMonth ? this.overrides.setMonth(date, month) : setMonth(date, month);
    };
    /**
     * Sets the year of the given date.
     *
     * @param date The date to set the year on.
     * @param year The year to set.
     * @returns The new date with the year set.
     */
    this.setYear = (date, year) => {
      var _this$overrides30;
      return (_this$overrides30 = this.overrides) !== null && _this$overrides30 !== void 0 && _this$overrides30.setYear ? this.overrides.setYear(date, year) : setYear(date, year);
    };
    /**
     * Returns the start of the broadcast week for the given date.
     *
     * @param date The original date.
     * @returns The start of the broadcast week.
     */
    this.startOfBroadcastWeek = (date, _dateLib) => {
      var _this$overrides31;
      return (_this$overrides31 = this.overrides) !== null && _this$overrides31 !== void 0 && _this$overrides31.startOfBroadcastWeek ? this.overrides.startOfBroadcastWeek(date, this) : startOfBroadcastWeek(date, this);
    };
    /**
     * Returns the start of the day for the given date.
     *
     * @param date The original date.
     * @returns The start of the day.
     */
    this.startOfDay = date => {
      var _this$overrides32;
      return (_this$overrides32 = this.overrides) !== null && _this$overrides32 !== void 0 && _this$overrides32.startOfDay ? this.overrides.startOfDay(date) : startOfDay(date);
    };
    /**
     * Returns the start of the ISO week for the given date.
     *
     * @param date The original date.
     * @returns The start of the ISO week.
     */
    this.startOfISOWeek = date => {
      var _this$overrides33;
      return (_this$overrides33 = this.overrides) !== null && _this$overrides33 !== void 0 && _this$overrides33.startOfISOWeek ? this.overrides.startOfISOWeek(date) : startOfISOWeek(date);
    };
    /**
     * Returns the start of the month for the given date.
     *
     * @param date The original date.
     * @returns The start of the month.
     */
    this.startOfMonth = date => {
      var _this$overrides34;
      return (_this$overrides34 = this.overrides) !== null && _this$overrides34 !== void 0 && _this$overrides34.startOfMonth ? this.overrides.startOfMonth(date) : startOfMonth(date);
    };
    /**
     * Returns the start of the week for the given date.
     *
     * @param date The original date.
     * @returns The start of the week.
     */
    this.startOfWeek = (date, _options) => {
      var _this$overrides35;
      return (_this$overrides35 = this.overrides) !== null && _this$overrides35 !== void 0 && _this$overrides35.startOfWeek ? this.overrides.startOfWeek(date, this.options) : startOfWeek(date, this.options);
    };
    /**
     * Returns the start of the year for the given date.
     *
     * @param date The original date.
     * @returns The start of the year.
     */
    this.startOfYear = date => {
      var _this$overrides36;
      return (_this$overrides36 = this.overrides) !== null && _this$overrides36 !== void 0 && _this$overrides36.startOfYear ? this.overrides.startOfYear(date) : startOfYear(date);
    };
    this.options = _objectSpread({
      locale: enUS
    }, options);
    this.overrides = overrides;
  }
  /**
   * Generates a mapping of Arabic digits (0-9) to the target numbering system
   * digits.
   *
   * @since 9.5.0
   * @returns A record mapping Arabic digits to the target numerals.
   */
  getDigitMap() {
    const {
      numerals = "latn"
    } = this.options;
    // Use Intl.NumberFormat to create a formatter with the specified numbering system
    const formatter = new Intl.NumberFormat("en-US", {
      numberingSystem: numerals
    });
    // Map Arabic digits (0-9) to the target numerals
    const digitMap = {};
    for (let i = 0; i < 10; i++) {
      digitMap[i.toString()] = formatter.format(i);
    }
    return digitMap;
  }
  /**
   * Replaces Arabic digits in a string with the target numbering system digits.
   *
   * @since 9.5.0
   * @param input The string containing Arabic digits.
   * @returns The string with digits replaced.
   */
  replaceDigits(input) {
    const digitMap = this.getDigitMap();
    return input.replace(/\d/g, digit => digitMap[digit] || digit);
  }
  /**
   * Formats a number using the configured numbering system.
   *
   * @since 9.5.0
   * @param value The number to format.
   * @returns The formatted number as a string.
   */
  formatNumber(value) {
    return this.replaceDigits(value.toString());
  }
  /**
   * Returns the preferred ordering for month and year labels for the current
   * locale.
   */
  getMonthYearOrder() {
    var _this$options$locale;
    const code = (_this$options$locale = this.options.locale) === null || _this$options$locale === void 0 ? void 0 : _this$options$locale.code;
    if (!code) {
      return "month-first";
    }
    return DateLib.yearFirstLocales.has(code) ? "year-first" : "month-first";
  }
  /**
   * Formats the month/year pair respecting locale conventions.
   *
   * @since 9.11.0
   */
  formatMonthYear(date) {
    const {
      locale,
      timeZone,
      numerals
    } = this.options;
    const localeCode = locale === null || locale === void 0 ? void 0 : locale.code;
    if (localeCode && DateLib.yearFirstLocales.has(localeCode)) {
      try {
        const intl = new Intl.DateTimeFormat(localeCode, {
          month: "long",
          year: "numeric",
          timeZone,
          numberingSystem: numerals
        });
        const formatted = intl.format(date);
        return formatted;
      } catch (_unused) {
        // Fallback to date-fns formatting below.
      }
    }
    const pattern = this.getMonthYearOrder() === "year-first" ? "y LLLL" : "LLLL y";
    return this.format(date, pattern);
  }
}
DateLib.yearFirstLocales = new Set(["eu", "hu", "ja", "ja-Hira", "ja-JP", "ko", "ko-KR", "lt", "lt-LT", "lv", "lv-LV", "mn", "mn-MN", "zh", "zh-CN", "zh-HK", "zh-TW"]);
/** The default locale (English). */
export { enUS as defaultLocale } from "date-fns/locale/en-US";
/**
 * The default date library with English locale.
 *
 * @since 9.2.0
 */
export const defaultDateLib = new DateLib();
/**
 * @ignore
 * @deprecated Use `defaultDateLib`.
 */
export const dateLib = defaultDateLib;